import React, { useEffect, useState } from "react";
import { BPMFileInput, Button, ButtonFrameworkVariant, ButtonType } from "../Components";
import "./ImageUploadPreview.scss";
import { MdOutlineDeleteOutline } from "react-icons/md";

interface ImageUploadPreviewProps {
  label: string; // like "Square Logo Dark Background"
  filename: string; // like "square_logo_dark.png"
  localUrl?: string; // local file object if available
  s3Url?: string; // s3url if available
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
  disabled?: boolean;
  type: "Client Logos" | "Client Creative Thumbnail" | "Client Intro Slide" | "Network Logos";
}

const ImageUploadPreview: React.FC<ImageUploadPreviewProps> = ({
  label,
  filename,
  localUrl,
  s3Url,
  onChange,
  onRemove,
  disabled,
  type,
}) => {
  const [imgError, setImgError] = useState(false);
  const hasS3Image = s3Url && !imgError;
  const hasImage = localUrl || hasS3Image;

  useEffect(() => {
    setImgError(false);
  }, [localUrl, s3Url]);

  const isDarkVariant = label?.includes("Dark");
  return (
    <div className="fileInputAndLabel">
      <div className="fileInputLabel">{label}</div>
      <div className="fileInputBorder">
        <BPMFileInput
          className="fileInput"
          buttonText={hasS3Image ? "Browse Files to Replace" : "Browse Files"}
          id={`cdnFileInput-${filename}`}
          name={`cdnFileInput-${filename}`}
          key={filename + (localUrl || s3Url || "")} // force a re-render when the file changes
          onChange={onChange}
          type={ButtonType.FILLED}
          disabled={disabled}
        />
      </div>
      <div className={`fileContainer${hasImage ? " has-image" : ""}`}>
        {hasImage ? (
          <>
            <div className="fileContainerHeader">
              <div className="imageFilename">{filename}</div>
              {localUrl && (
                <Button
                  className="imageDelete"
                  type={ButtonType.EMPTY}
                  icon={<MdOutlineDeleteOutline />}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  onClick={onRemove}
                />
              )}
            </div>
            <div className="imageAndPreviewContainer">
              <div className={`imageContainer${isDarkVariant ? " dark-bg" : ""}`}>
                <img
                  className={
                    type === "Client Logos"
                      ? "client-logo"
                      : type === "Client Creative Thumbnail"
                      ? "client-creative-thumbnail"
                      : type === "Client Intro Slide"
                      ? "client-intro-slide"
                      : type === "Network Logos"
                      ? "network-logo"
                      : ""
                  }
                  src={localUrl || s3Url}
                  alt={label}
                  onError={() => setImgError(true)}
                />
              </div>
              {(type === "Client Logos" || type === "Network Logos") && (localUrl || s3Url) && (
                <div className={`logo-actual-size-preview${isDarkVariant ? " dark-bg" : ""}`}>
                  <img
                    className="logo-actual-size-img"
                    src={localUrl || s3Url}
                    alt="Actual Size Preview"
                    style={{
                      width: type === "Client Logos" ? (filename.includes("square") ? 40 : 85) : 40,
                      height: 40,
                      objectFit: "contain",
                      background: "transparent",
                      display: "block",
                    }}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="filePlaceholderContainer">
            <div className="filePlaceholderTitle">Uploaded File</div>
            <div className="filePlaceholderText">Upload a file above.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploadPreview;
